import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Send, MessageSquare, ArrowLeft } from "lucide-react";

export default function Messages() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [other, setOther] = useState(null);
  const [text, setText] = useState("");
  const end = useRef(null);
  const { data } = useQuery({ queryKey: ["threads"], queryFn: async () => (await api.get("/api/messages")).data, refetchInterval: 15000 });
  const { data: msgs = [] } = useQuery({ queryKey: ["thread", other?.user_id], enabled: !!other,
    queryFn: async () => (await api.get(`/api/messages/${other.user_id}`)).data, refetchInterval: 8000 });
  // scroll only the conversation pane, never the whole page
  useEffect(() => { const el = end.current?.parentElement; if (el) el.scrollTop = el.scrollHeight; }, [msgs.length, other]);
  useEffect(() => { if (other) { qc.invalidateQueries({ queryKey: ["unread"] }); qc.invalidateQueries({ queryKey: ["threads"] }); } }, [other, msgs.length, qc]);
  const send = async () => {
    if (!text.trim()) return;
    try { await api.post(`/api/messages/${other.user_id}`, { text }); setText(""); qc.invalidateQueries({ queryKey: ["thread", other.user_id] }); qc.invalidateQueries({ queryKey: ["threads"] }); }
    catch (err) { toast.error(apiErr(err)); }
  };
  if (!user?.club_id) {
    return <div className="p-8"><p className="surface rounded-2xl p-6 text-center text-sm text-[#8A91A6]">Messages connect you with your club's coaches. Join a club to start a conversation.</p></div>;
  }
  const threads = data?.threads || [];
  const known = new Set(threads.map((t) => t.user_id));
  const contacts = (data?.contacts || []).filter((c) => !known.has(c.user_id));
  return (
    <div className="p-4 md:p-8">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 h-[calc(100dvh-10rem)] md:h-[calc(100vh-4rem)]">
        <div className={`md:col-span-4 surface rounded-2xl p-3 overflow-y-auto ${other ? "hidden md:block" : ""}`} data-testid="threads">
          <p className="font-heading font-black text-xl px-2 py-2">Messages</p>
          {threads.map((t) => (
            <button key={t.user_id} onClick={() => setOther(t)} className={`w-full text-left rounded-xl px-3 py-2.5 flex items-center gap-3 ${other?.user_id === t.user_id ? "bg-acid/10" : "hover:bg-white/5"}`}>
              <span className="w-9 h-9 rounded-full bg-neural/20 border border-neural/40 flex items-center justify-center text-sm font-bold">{t.name[0]}</span>
              <div className="min-w-0 flex-1"><p className="text-sm font-semibold truncate">{t.name} {["owner", "coach"].includes(t.role) && <span className="mono-label text-acid">coach</span>}</p><p className="text-xs text-[#8A91A6] truncate">{t.last}</p></div>
              {t.unread > 0 && <span className="w-5 h-5 rounded-full bg-acid text-black text-xs font-bold flex items-center justify-center">{t.unread}</span>}
            </button>
          ))}
          {contacts.length > 0 && <p className="mono-label px-2 mt-4 mb-1">start a conversation</p>}
          {contacts.map((c) => (
            <button key={c.user_id} onClick={() => setOther(c)} className="w-full text-left rounded-xl px-3 py-2 flex items-center gap-3 hover:bg-white/5">
              <span className="w-8 h-8 rounded-full bg-[#1E222A] flex items-center justify-center text-xs">{c.name[0]}</span>
              <span className="text-sm">{c.name}</span>{["owner", "coach"].includes(c.role) && <span className="mono-label text-acid">coach</span>}
            </button>
          ))}
        </div>
        <div className={`md:col-span-8 surface rounded-2xl flex flex-col min-h-0 ${other ? "" : "hidden md:flex"}`}>
          {!other ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-6">
              <MessageSquare className="w-10 h-10 text-[#343A4A]" />
              <p className="text-sm text-[#8A91A6] mt-2">Choose a conversation. Questions about scaling, technique or your programme go straight to your coach.</p>
            </div>
          ) : (
            <>
              <div className="p-4 border-b border-[#1E222A] flex items-center gap-3">
                <button onClick={() => setOther(null)} className="md:hidden" aria-label="Back"><ArrowLeft className="w-5 h-5" /></button>
                <p className="font-heading font-bold">{other.name}</p>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-2" data-testid="thread">
                {msgs.map((m) => {
                  const mine = m.from === user.user_id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${mine ? "bg-acid text-black rounded-br-sm" : "bg-[#171A21] rounded-bl-sm"}`}>
                        <p className="whitespace-pre-line">{m.text}</p>
                        <p className={`text-[10px] mt-1 ${mine ? "text-black/60" : "text-[#565C6F]"}`}>{new Date(m.at).toLocaleString(undefined, { weekday: "short", hour: "2-digit", minute: "2-digit" })}</p>
                      </div>
                    </div>
                  );
                })}
                <div ref={end} />
              </div>
              <div className="p-3 border-t border-[#1E222A] flex gap-2">
                <input className="flex-1 h-11 rounded-full bg-[#0B0D11] border border-[#1E222A] px-4 text-sm" placeholder="Write a message" value={text}
                  onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="msg-input" />
                <button onClick={send} className="w-11 h-11 rounded-full bg-acid text-black flex items-center justify-center" aria-label="Send" data-testid="msg-send"><Send className="w-4 h-4" /></button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
